import { getPanelScrollTopInset, PANEL_SCROLL_TOP_INSET_FALLBACK_PX } from 'lib/panelEndGutter'

// How much of the trip is animated rather than cut. Applied as a signed offset
// from the target (see getAnswerTargetScrollPlan), so it is the same magnitude in
// both directions and a round trip reads as one hand.
export const ANSWER_TARGET_SCROLL_LEAD_PX = 112
export const ANSWER_TARGET_SCROLL_DURATION_MS = 560
export const ANSWER_TARGET_SCROLL_TOLERANCE_PX = 80
// The floating thread capsule occupies the first 40px of the panel. Leave an
// additional 8px so an addressed post is visibly clear of the control.
export const ANSWER_TARGET_TOP_INSET_PX = PANEL_SCROLL_TOP_INSET_FALLBACK_PX

const TITLE_ANSWER_SELECTOR = '[data-answer-role="title"]'
const QUEST_FRAME_SELECTOR = '[data-qa-scroll-quest-frame="true"]'
const ANSWER_BREADCRUMB_SELECTOR = '[data-answer-breadcrumb="true"]'
const ANSWER_SURFACE_SELECTOR = '[data-testid="answer-root"]'

type AnswerTargetScrollMetrics = {
    scrollTop: number
    scrollHeight: number
    clientHeight: number
    targetTop: number
    topInset?: number
}

type AnswerTargetScrollPlanInput = {
    currentScrollTop: number
    finalScrollTop: number
    maxScrollTop: number
    leadOffsetPx?: number
    tolerancePx?: number
    forceReveal?: boolean
}

type ScrollAnswerTargetOptions = {
    durationMs?: number
    leadOffsetPx?: number
    tolerancePx?: number
    forceReveal?: boolean
    shouldCancel?: () => boolean
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max))

const nextFrame = () =>
    new Promise<void>(resolve => {
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
            window.requestAnimationFrame(() => resolve())
            return
        }

        setTimeout(resolve, 0)
    })

const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3)

const prefersReducedMotion = () => {
    try {
        return (
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches
        )
    } catch {
        return false
    }
}

export function answerTargetSelector(answerId: string) {
    return `[data-testid="answer-root"][data-answer-id="${String(answerId).replace(/"/g, '\\"')}"]`
}

export function findAnswerTargetElement(
    root: ParentNode | null | undefined,
    answerId: string,
): HTMLElement | null {
    return (
        (root?.querySelector?.(`.answer-in-view-${answerId}`) as HTMLElement | null) ||
        (root?.querySelector?.(answerTargetSelector(answerId)) as HTMLElement | null) ||
        null
    )
}

// A branch's head post has thread chrome immediately above it. Align that chrome,
// rather than the post body, so the breadcrumb clears the floating panel controls.
// Tail posts have no owned breadcrumb and continue to align by their own top edge.
export function getAnswerTargetAlignmentElement(targetElement: HTMLElement): HTMLElement {
    const answerSurface = targetElement.matches?.(ANSWER_SURFACE_SELECTOR)
        ? targetElement
        : targetElement.querySelector?.<HTMLElement>(ANSWER_SURFACE_SELECTOR)
    if (!answerSurface?.closest?.(TITLE_ANSWER_SELECTOR)) return targetElement

    return (
        targetElement
            .closest(QUEST_FRAME_SELECTOR)
            ?.querySelector<HTMLElement>(ANSWER_BREADCRUMB_SELECTOR) || targetElement
    )
}

export function calculateAnswerTargetScrollTop({
    scrollTop,
    scrollHeight,
    clientHeight,
    targetTop,
    topInset = ANSWER_TARGET_TOP_INSET_PX,
}: AnswerTargetScrollMetrics) {
    const maxScrollTop = Math.max(0, scrollHeight - clientHeight)
    const targetAbsoluteTop = scrollTop + targetTop
    const insetTop = targetAbsoluteTop - topInset

    return clamp(insetTop, 0, maxScrollTop)
}

// dm_chat: the conversation's latest message rests at the bottom, above the
// composer, the way a chat opens — the same resting point as
// PanelState.scrollToBottom (the thread's blank bottom gutter stays below view).
const DM_CHAT_LAST_SELECTOR = '[data-dm-chat-last="true"]'

function isDmChatLastMessage(targetElement: HTMLElement): boolean {
    return !!(
        targetElement.closest?.(DM_CHAT_LAST_SELECTOR) ||
        targetElement.querySelector?.(DM_CHAT_LAST_SELECTOR)
    )
}

export function getAnswerTargetScrollTop(scrollContainer: HTMLElement, targetElement: HTMLElement) {
    if (isDmChatLastMessage(targetElement)) {
        const bottomGutter = scrollContainer.querySelector<HTMLElement>(
            '[data-thread-bottom-gutter="true"]',
        )
        return Math.max(
            0,
            (scrollContainer.scrollHeight || 0) -
                (scrollContainer.clientHeight || 0) -
                (bottomGutter?.offsetHeight || 0),
        )
    }
    const alignmentElement = getAnswerTargetAlignmentElement(targetElement)
    const targetRect = alignmentElement.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()

    return calculateAnswerTargetScrollTop({
        scrollTop: scrollContainer.scrollTop || 0,
        scrollHeight: scrollContainer.scrollHeight || 0,
        clientHeight: scrollContainer.clientHeight || 0,
        targetTop: (targetRect.top || 0) - (containerRect.top || 0),
        topInset: getPanelScrollTopInset(scrollContainer),
    })
}

// The reveal cuts instantly to a staged position a lead short of the target and
// animates only that last leg, so the distance travelled is bounded no matter how
// far the target is. The lead is SIGNED: it is subtracted going down and added
// going up, so the animated leg always continues the trip's own direction.
//
// Getting that sign wrong is not a detail. With the lead always subtracted, an
// upward trip cut to a point PAST the target and then animated back down — the
// long move read as directionless (an instant cut has no direction), and the only
// motion the eye could read ran opposite to the trip. Going up now means the
// target descends from the top edge into its resting inset, which is what the last
// 112px of scrolling up to it by hand looks like.
//
// Direction is taken from the trip itself rather than from the caller, because the
// caller does not know it: a quote row points backwards in the thread, but whether
// that is above or below the reader depends on where the reader is standing.
export function getAnswerTargetScrollPlan({
    currentScrollTop,
    finalScrollTop,
    maxScrollTop,
    leadOffsetPx = ANSWER_TARGET_SCROLL_LEAD_PX,
    tolerancePx = 2,
    forceReveal = false,
}: AnswerTargetScrollPlanInput) {
    const finalTop = clamp(finalScrollTop, 0, maxScrollTop)
    const currentAlreadyAtFinal = Math.abs(currentScrollTop - finalTop) <= tolerancePx
    // Inside the settle tolerance there is no trip to continue. A forced re-reveal
    // from rest keeps the original downward lead: the target is already on screen,
    // and rising into place keeps it there for the whole leg.
    const travellingUp = currentScrollTop - finalTop > tolerancePx
    const leadTop = travellingUp ? finalTop + leadOffsetPx : finalTop - leadOffsetPx
    // Never stage outside the stretch the trip already covers. A trip shorter than
    // its own lead would otherwise cut backwards past the reader and reverse on the
    // animated leg — the same defect, reintroduced at small scale.
    const stagedTop = clamp(
        currentAlreadyAtFinal
            ? leadTop
            : travellingUp
            ? Math.min(leadTop, currentScrollTop)
            : Math.max(leadTop, currentScrollTop),
        0,
        maxScrollTop,
    )
    const hasRoomForReveal = Math.abs(stagedTop - finalTop) > tolerancePx
    const shouldAnimate = hasRoomForReveal && (forceReveal || !currentAlreadyAtFinal)

    return {
        startScrollTop: shouldAnimate ? stagedTop : currentScrollTop,
        finalScrollTop: finalTop,
        shouldAnimate,
    }
}

function animateScrollTop(
    scrollContainer: HTMLElement,
    targetTop: number,
    durationMs: number,
    shouldCancel?: () => boolean,
): Promise<boolean> {
    if (
        typeof window === 'undefined' ||
        typeof window.requestAnimationFrame !== 'function' ||
        durationMs <= 0 ||
        prefersReducedMotion()
    ) {
        if (shouldCancel?.()) return Promise.resolve(false)
        scrollContainer.scrollTop = targetTop
        return Promise.resolve(true)
    }

    const startTop = scrollContainer.scrollTop || 0
    const distance = targetTop - startTop
    if (Math.abs(distance) <= 1) {
        if (shouldCancel?.()) return Promise.resolve(false)
        scrollContainer.scrollTop = targetTop
        return Promise.resolve(true)
    }

    return new Promise(resolve => {
        let startedAt: number | null = null

        const tick = (timestamp: number) => {
            if (shouldCancel?.()) {
                resolve(false)
                return
            }

            if (startedAt == null) startedAt = timestamp

            const progress = clamp((timestamp - startedAt) / durationMs, 0, 1)
            scrollContainer.scrollTop = startTop + distance * easeOutCubic(progress)

            if (progress < 1) {
                window.requestAnimationFrame(tick)
                return
            }

            scrollContainer.scrollTop = targetTop
            resolve(true)
        }

        window.requestAnimationFrame(tick)
    })
}

export function isAnswerElementNearTarget(
    scrollContainer: HTMLElement,
    targetElement: HTMLElement | null | undefined,
    tolerancePx = ANSWER_TARGET_SCROLL_TOLERANCE_PX,
) {
    if (!targetElement) return false

    const targetScrollTop = getAnswerTargetScrollTop(scrollContainer, targetElement)

    return Math.abs((scrollContainer.scrollTop || 0) - targetScrollTop) <= tolerancePx
}

export async function scrollAnswerElementIntoView(
    scrollContainer: HTMLElement,
    targetElement: HTMLElement,
    options: ScrollAnswerTargetOptions = {},
) {
    if (options.shouldCancel?.()) return false

    const durationMs = options.durationMs ?? ANSWER_TARGET_SCROLL_DURATION_MS
    const tolerancePx = options.tolerancePx ?? ANSWER_TARGET_SCROLL_TOLERANCE_PX
    const maxScrollTop = Math.max(
        0,
        (scrollContainer.scrollHeight || 0) - (scrollContainer.clientHeight || 0),
    )
    const finalScrollTop = getAnswerTargetScrollTop(scrollContainer, targetElement)
    const plan = getAnswerTargetScrollPlan({
        currentScrollTop: scrollContainer.scrollTop || 0,
        finalScrollTop,
        maxScrollTop,
        leadOffsetPx: options.leadOffsetPx,
        tolerancePx,
        forceReveal: options.forceReveal ?? true,
    })

    if (!plan.shouldAnimate || prefersReducedMotion()) {
        if (options.shouldCancel?.()) return false
        if (isAnswerElementNearTarget(scrollContainer, targetElement, tolerancePx)) return true

        scrollContainer.scrollTop = plan.finalScrollTop
        return isAnswerElementNearTarget(scrollContainer, targetElement, tolerancePx)
    }

    scrollContainer.scrollTop = plan.startScrollTop
    await nextFrame()
    if (options.shouldCancel?.()) return false
    const completed = await animateScrollTop(
        scrollContainer,
        plan.finalScrollTop,
        durationMs,
        options.shouldCancel,
    )
    if (!completed) return false

    return isAnswerElementNearTarget(scrollContainer, targetElement, tolerancePx)
}
